import React, { useRef, useState, useContext, forwardRef } from 'react';
import { makeId, warning, forwardRefWithAs, useForkedRef, wrapEvent, createNamedContext } from '@reach/utils';
import { useId } from '@reach/auto-id';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var DisclosureContext = /*#__PURE__*/createNamedContext("DisclosureContext", {});

var useDisclosureContext = function useDisclosureContext() {
  return useContext(DisclosureContext);
}; ////////////////////////////////////////////////////////////////////////////////


var DisclosureStates;

(function (DisclosureStates) {
  DisclosureStates["Open"] = "open";
  DisclosureStates["Collapsed"] = "collapsed";
})(DisclosureStates || (DisclosureStates = {})); ////////////////////////////////////////////////////////////////////////////////

/**
 * Disclosure
 *
 * The wrapper component and context provider for a disclosure's button and
 * panel components. A disclosure should only have one button and one panel
 * descendant.
 *
 * @see Docs https://reacttraining.com/reach-ui/disclosure#disclosure-1
 *
 * @param props
 */


var Disclosure = function Disclosure(_ref) {
  var children = _ref.children,
      _ref$defaultOpen = _ref.defaultOpen,
      defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
      onChange = _ref.onChange,
      openProp = _ref.open,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "defaultOpen", "onChange", "open"]);
  /*
   * You shouldn't switch between controlled/uncontrolled. We'll check for a
   * controlled component and track any changes in a ref to show a warning.
   */


  var wasControlled = openProp != null;

  var _useRef = useRef(wasControlled),
      isControlled = _useRef.current;

  var id = useId(props.id != null ? String(props.id) : undefined) || "disclosure";
  var panelId = makeId("panel", id);

  var _useState = useState(isControlled ? openProp : defaultOpen),
      open = _useState[0],
      setOpen = _useState[1];

  if (process.env.NODE_ENV !== "production") {
    process.env.NODE_ENV !== "production" ? warning(!(isControlled && !wasControlled || !isControlled && wasControlled), "Disclosure is changing from controlled to uncontrolled. Disclosure should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled Disclosure for the lifetime of the component. Check the `open` prop being passed in.") : void 0;
  }

  function onSelect() {
    onChange && onChange();

    if (!isControlled) {
      setOpen(!open);
    }
  }

  var context = {
    disclosureId: id,
    onSelect: onSelect,
    open: open,
    panelId: panelId
  };

  if (isControlled && openProp !== open) {
    /*
     * If the component is controlled, we'll sync internal state with the
     * controlled state
     */
    setOpen(openProp);
  }

  return React.createElement(DisclosureContext.Provider, {
    value: context
  }, children);
};

if (process.env.NODE_ENV !== "production") {
  Disclosure.displayName = "Disclosure";
} ////////////////////////////////////////////////////////////////////////////////

/**
 * DisclosureButton
 *
 * The trigger button a user clicks to interact with a disclosure.
 *
 * @see Docs https://reacttraining.com/reach-ui/disclosure#disclosurebutton
 */


var DisclosureButton = /*#__PURE__*/forwardRefWithAs(function DisclosureButton(_ref2, forwardedRef) {
  var _ref2$as = _ref2.as,
      Comp = _ref2$as === void 0 ? "button" : _ref2$as,
      children = _ref2.children,
      onClick = _ref2.onClick,
      onMouseDown = _ref2.onMouseDown,
      onPointerDown = _ref2.onPointerDown,
      props = _objectWithoutPropertiesLoose(_ref2, ["as", "children", "onClick", "onMouseDown", "onPointerDown"]);

  var _useDisclosureContext = useDisclosureContext(),
      onSelect = _useDisclosureContext.onSelect,
      open = _useDisclosureContext.open,
      panelId = _useDisclosureContext.panelId;

  var ownRef = useRef(null);
  var ref = useForkedRef(forwardedRef, ownRef);

  function handleClick(event) {
    event.preventDefault();
    ownRef.current && ownRef.current.focus();
    onSelect();
  }

  return React.createElement(Comp // Optionally, the element with role `button` has a value specified for
  // `aria-controls` that refers to the element that contains all the
  // content that is shown or hidden.
  // https://www.w3.org/TR/wai-aria-practices-1.2/#disclosure
  , Object.assign({
    "aria-controls": panelId,
    "aria-expanded": open
  }, props, {
    "data-reach-disclosure-button": "",
    "data-state": open ? DisclosureStates.Open : DisclosureStates.Collapsed,
    ref: ref,
    onClick: wrapEvent(onClick, handleClick)
  }), children);
});

if (process.env.NODE_ENV !== "production") {
  DisclosureButton.displayName = "DisclosureButton";
} ////////////////////////////////////////////////////////////////////////////////

/**
 * DisclosurePanel
 *
 * The collapsible panel in which inner content for an disclosure item is
 * rendered.
 *
 * @see Docs https://reacttraining.com/reach-ui/disclosure#disclosurepanel
 */


var DisclosurePanel = /*#__PURE__*/forwardRef(function DisclosurePanel(_ref3, forwardedRef) {
  var children = _ref3.children,
      props = _objectWithoutPropertiesLoose(_ref3, ["children"]);

  var _useDisclosureContext2 = useDisclosureContext(),
      panelId = _useDisclosureContext2.panelId,
      open = _useDisclosureContext2.open;

  return React.createElement("div", Object.assign({
    ref: forwardedRef,
    hidden: !open
  }, props, {
    "data-reach-disclosure-panel": "",
    "data-state": open ? DisclosureStates.Open : DisclosureStates.Collapsed,
    id: panelId,
    tabIndex: -1
  }), children);
});

if (process.env.NODE_ENV !== "production") {
  DisclosurePanel.displayName = "DisclosurePanel";
}

export { Disclosure, DisclosureButton, DisclosurePanel, DisclosureStates };